'use client';

import * as React from 'react';
import { ARIA_LABEL_SHARE_PROPERTY } from '@federa/const/aria-label-values';
import { semanticActions } from '@federa/components/icons/semantic-tokens';
import { colors } from '@federa/components/css-refs.colors';
import { spacing } from '@federa/components/css-refs.spacing';
import { motion } from '@federa/components/css-refs.effects';
import { PropertyAddressBlock } from '@federa/components/data-display/PropertyAddressBlock';
import { SemanticIcon } from '@federa/components/icons/SemanticIcon';
import {
	FLEX_CENTER_STYLE,
	FLEX_COLUMN_STYLE,
	FLEX_ROW_CENTER_STYLE,
	FLEX_ROW_START_STYLE,
	INLINE_FLEX_ROW_CENTER_STYLE,
} from '@federa/const/style-flex-presets';
import { Button } from '../primitives/Button.tsx';
import { DETAILS_PADDING_STYLES, getAddressRowStyles, propertyCardTokens } from './property-card-styles.ts';
import type { IconColor } from '@federa/types/ui-icons';
import type { PropertyCardPriceDisplayFields, PropertyCardProps } from '@federa/types/ui-property-card';

/** Icon-only ghost button for the card's save/share row, named with its `PropertyCard*` siblings below. NOT
 * `ActionButton`: two other modules EXPORT that spelling over different contracts -- the connect desk's label-swapping
 * pending button (`components/connect/kit/Controls.tsx`) and a labelled busy-state button
 * (`spa/market-maker/frontend/mm/[id]/page.progress.primitives.tsx`). */
function PropertyCardActionButton({
	onClick,
	isHovered,
	setHovered,
	baseOpacity,
	ariaLabel,
	iconName,
	iconColor,
}: {
	onClick: (e: React.MouseEvent) => void;
	isHovered: boolean;
	setHovered: (hovered: boolean) => void;
	baseOpacity: number;
	ariaLabel: string;
	iconName: 'bookmark-solid' | 'bookmark' | 'share';
	// The `color` prop this forwards to `SemanticIcon`, taken from that prop's own type rather than re-spelled as the
	// two members this card happens to pass. The inline `'primary' | 'secondary'` was byte-equal to `SpinnerVariant`
	// and QUAL-INLINE-LITERAL-UNION-DUP-001 reported it as that alias -- a WRONG derivation (a spinner variant is not
	// an icon colour), but the restatement it found was real: the members are a subset of `IconColor`, so an icon
	// colour retired there left this narrowing spelling a value the renderer no longer accepts.
	iconColor: IconColor;
}) {
	return (
		<Button
			variant="ghost"
			size="sm"
			icon={iconName}
			// The saved bookmark carries the BRAND tone while the share glyph stays secondary, and the shared icon renderer
			// is pinned to `color="inherit"`, so this button brings its own renderer rather than losing the distinction.
			renderIconFn={() => <SemanticIcon name={iconName} size="sm" color={iconColor} />}
			onClick={onClick}
			onMouseEnter={() => setHovered(true)}
			onMouseLeave={() => setHovered(false)}
			style={{
				...FLEX_CENTER_STYLE,
				width: spacing[6],
				height: spacing[6],
				transition: `all ${motion.duration.fast} ${motion.easing.standard}`,
				opacity: isHovered ? 1 : baseOpacity,
				color: colors.text.secondary,
			}}
			aria-label={ariaLabel}
		/>
	);
}

function PropertyCardPrice({ priceLabel, priceAmount, showCountdown, countdownLabel, countdownColor }: PropertyCardPriceDisplayFields) {
	return (
		<div
			style={{
				...FLEX_ROW_CENTER_STYLE,
				gap: propertyCardTokens.layout.priceInlineGap,
				flexWrap: 'wrap',
			}}
		>
			<div
				style={{
					display: 'flex',
					alignItems: 'baseline',
					gap: propertyCardTokens.layout.priceInlineGap,
				}}
			>
				{priceLabel && <span style={propertyCardTokens.text.priceText}>{priceLabel}</span>}
				<span style={propertyCardTokens.text.priceText}>{priceAmount}</span>
			</div>
			{showCountdown && countdownLabel && (
				<div
					style={{
						...INLINE_FLEX_ROW_CENTER_STYLE,
						gap: propertyCardTokens.countdown.gap,
						fontSize: propertyCardTokens.countdown.fontSize,
						fontWeight: propertyCardTokens.countdown.fontWeight,
						letterSpacing: propertyCardTokens.countdown.letterSpacing,
						color: countdownColor,
					}}
				>
					<SemanticIcon name="clock" size="xs" color="inherit" />
					<span>{countdownLabel}</span>
				</div>
			)}
		</div>
	);
}

interface ActionHoverState {
	hovered: boolean;
	setHovered: (hovered: boolean) => void;
}

/** Props for click and hover state shared between actions and details */
interface CardActionProps {
	onSave?: (id: string) => void;
	onShare?: (id: string) => void;
	isSaved: boolean;
	handleSaveClick: (e: React.MouseEvent) => void;
	handleShareClick: (e: React.MouseEvent) => void;
	saveHover: ActionHoverState;
	shareHover: ActionHoverState;
}

function PropertyCardActions({ onSave, onShare, isSaved, handleSaveClick, handleShareClick, saveHover, shareHover }: CardActionProps) {
	if (!onSave && !onShare) return null;
	return (
		<div
			style={{
				...FLEX_ROW_START_STYLE,
				gap: propertyCardTokens.layout.actionGroupGap,
				flexShrink: 0,
			}}
		>
			{onSave && (
				<PropertyCardActionButton
					onClick={handleSaveClick}
					isHovered={saveHover.hovered}
					setHovered={saveHover.setHovered}
					baseOpacity={0.9}
					ariaLabel={semanticActions.icons[isSaved ? 'bookmark-solid' : 'bookmark'].ariaLabel}
					iconName={isSaved ? 'bookmark-solid' : 'bookmark'}
					iconColor={isSaved ? 'primary' : 'secondary'}
				/>
			)}
			{onShare && (
				<PropertyCardActionButton
					onClick={handleShareClick}
					isHovered={shareHover.hovered}
					setHovered={shareHover.setHovered}
					baseOpacity={0.5}
					ariaLabel={ARIA_LABEL_SHARE_PROPERTY}
					iconName="share"
					iconColor="secondary"
				/>
			)}
		</div>
	);
}

interface PropertyCardDetailsProps extends CardActionProps, PropertyCardPriceDisplayFields {
	property: PropertyCardProps['property'];
}

export function PropertyCardDetails({
	property,
	onSave,
	onShare,
	isSaved,
	priceLabel,
	priceAmount,
	showCountdown,
	countdownLabel,
	countdownColor,
	handleSaveClick,
	handleShareClick,
	saveHover,
	shareHover,
}: PropertyCardDetailsProps) {
	const hasActions = !!(onSave ?? onShare);

	return (
		<div style={FLEX_COLUMN_STYLE}>
			<div style={DETAILS_PADDING_STYLES}>
				<div style={getAddressRowStyles(hasActions)}>
					<PropertyAddressBlock address={property.address} city={property.city} state={property.state} />
					<PropertyCardActions
						onSave={onSave}
						onShare={onShare}
						isSaved={isSaved}
						handleSaveClick={handleSaveClick}
						handleShareClick={handleShareClick}
						saveHover={saveHover}
						shareHover={shareHover}
					/>
				</div>
				<PropertyCardPrice
					priceLabel={priceLabel}
					priceAmount={priceAmount}
					showCountdown={showCountdown}
					countdownLabel={countdownLabel}
					countdownColor={countdownColor}
				/>
			</div>
		</div>
	);
}
